import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Users, 
  Zap, 
  Play, 
  BarChart3, 
  FileUp, 
  MessageSquareText,
  ChevronRight,
  Lightbulb,
  CheckCircle2,
  ArrowRight,
  BookOpen,
  Download,
  AlertCircle,
  HelpCircle,
  Sparkles,
  TrendingUp,
  Target,
  Building2,
  ShoppingCart,
  ClipboardList,
  Upload
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useNavigate } from 'react-router-dom';

const STEPS = [
  {
    id: 1,
    title: 'Upload Your Population',
    icon: Users,
    color: 'bg-blue-50 text-blue-600',
    description: 'Start by uploading a CSV or Excel file containing your population data.',
    details: [
      'Include demographic columns (age, department, location, tenure, etc.)',
      'Each row represents one individual in your population',
      'SynPop automatically detects column types and validates data',
      'Minimum 10 rows recommended for meaningful simulations'
    ],
    action: { label: 'Go to Populations', path: '/populations' },
    expectedOutcome: 'Your population will be validated, column types inferred, and sample values displayed for verification.'
  },
  {
    id: 2,
    title: 'Create a Stimulus',
    icon: Zap,
    color: 'bg-amber-50 text-amber-600',
    description: 'Define the question or scenario you want to test with your population.',
    details: [
      'Choose between Closed-ended (multiple choice) or Open-ended (free text) responses',
      'For closed-ended: select a response scale (Likert, Yes/No, custom options)',
      'For open-ended: set response length and optionally specify language',
      'Add context documents (PDFs, policies) to help AI understand the scenario'
    ],
    action: { label: 'Go to Stimuli', path: '/stimuli' },
    expectedOutcome: 'A reusable stimulus template that can be run against any compatible population.'
  },
  {
    id: 3,
    title: 'Run Your Simulation',
    icon: Play,
    color: 'bg-emerald-50 text-emerald-600',
    description: 'Create a simulation by selecting your population and stimulus, then let AI generate realistic responses.',
    details: [
      'AI considers each individual\'s demographics to generate authentic responses',
      'For closed-ended: uses probabilistic sampling for diverse, realistic distributions',
      'For open-ended: generates unique comments with sentiment and theme analysis',
      'Simulation typically completes in 1-3 minutes depending on population size'
    ],
    action: { label: 'Go to Simulations', path: '/simulations' },
    expectedOutcome: 'Realistic AI-generated responses for every individual in your population, ready for analysis.'
  },
  {
    id: 4,
    title: 'Analyze Results',
    icon: BarChart3,
    color: 'bg-blue-50 text-blue-600',
    description: 'Explore your results through interactive charts, demographic breakdowns, and AI-generated insights.',
    details: [
      'View response distributions with semantic color coding',
      'Filter and breakdown results by demographic groups',
      'For open-ended: browse comments, explore themes, and see sentiment analysis',
      'Generate AI insights with custom questions about your results'
    ],
    action: { label: 'Go to Analysis', path: '/analysis' },
    expectedOutcome: 'Comprehensive analysis dashboard with charts, demographic breakdowns, and actionable insights.'
  }
];

const USE_CASES = [
  {
    id: 1,
    title: 'HR: Employee Engagement Survey',
    icon: Building2,
    scenario: 'An HR team wants to gauge employee reaction to a proposed 4-day workweek before announcing it.',
    population: {
      name: 'Company Employees',
      size: '1,500 employees',
      demographics: 'Age, department, tenure, job level, location, education'
    },
    stimulus: {
      question: 'How favorable would you be to introducing a 4-day workweek?',
      type: 'Closed-ended',
      scale: '5-point favorability (Very Unfavorable to Very Favorable)'
    },
    results: {
      headline: '68% favorable overall',
      insights: [
        'Highest support from employees under 35 (82% favorable)',
        'Engineering and Product teams most supportive (75%+)',
        'Finance team shows more resistance (48% favorable)',
        'Employees with 5+ years tenure slightly more cautious'
      ]
    },
    actionable: 'Target communication to Finance team addressing their concerns before rollout'
  },
  {
    id: 2,
    title: 'Marketing: Product Launch Sentiment',
    icon: ShoppingCart,
    scenario: 'A product team wants to test messaging for a new premium subscription tier.',
    population: {
      name: 'Customer Base',
      size: '5,000 customers',
      demographics: 'Age, income, purchase frequency, product interest, loyalty tier'
    },
    stimulus: {
      question: 'What are your thoughts on our new Premium Plus subscription?',
      type: 'Open-ended',
      scale: 'Free text (max 200 characters)'
    },
    results: {
      headline: '52% positive sentiment',
      insights: [
        'Value concerns from mid-tier customers (48% negative mentions)',
        'High-income customers excited about exclusive features',
        'Top themes: "Worth the price", "Too expensive", "Great perks"',
        'Younger demographics more willing to try'
      ]
    },
    actionable: 'Create mid-tier bridge offer and emphasize ROI in messaging'
  },
  {
    id: 3,
    title: 'Policy: Organizational Change Readiness',
    icon: Target,
    scenario: 'Leadership wants to assess readiness for a major org restructure.',
    population: {
      name: 'All Stakeholders',
      size: '800 managers and directors',
      demographics: 'Seniority, department, years at company, previous change experience'
    },
    stimulus: {
      question: 'To what extent do you support the proposed organizational restructure?',
      type: 'Closed-ended',
      scale: '7-point Likert (Strongly Oppose to Strongly Support)'
    },
    results: {
      headline: '41% supportive, 34% neutral, 25% opposed',
      insights: [
        'Directors more supportive (56%) than mid-level managers (32%)',
        'Teams with previous successful changes more open (62%)',
        'Concerns highest in departments with most disruption',
        'Neutral group key to success - needs more information'
      ]
    },
    actionable: 'Focus change management efforts on mid-level managers with tailored FAQs'
  },
  {
    id: 4,
    title: 'Research: Market Segmentation Study',
    icon: TrendingUp,
    scenario: 'A research team needs to identify distinct customer segments for targeting.',
    population: {
      name: 'Market Panel',
      size: '2,000 respondents',
      demographics: 'Age, income, lifestyle, tech adoption, media consumption'
    },
    stimulus: {
      question: 'Which of these factors most influences your purchasing decisions?',
      type: 'Closed-ended',
      scale: 'Multiple choice (Price, Quality, Brand, Convenience, Sustainability)'
    },
    results: {
      headline: '5 distinct segments identified',
      insights: [
        'Value Seekers (32%): Price-driven, younger demographics',
        'Quality First (28%): Higher income, prioritize brand and quality',
        'Eco-conscious (18%): Sustainability paramount, willing to pay more',
        'Convenience Focused (15%): Time-poor professionals',
        'Brand Loyalists (7%): Established preferences'
      ]
    },
    actionable: 'Develop targeted campaigns for top 3 segments with customized messaging'
  }
];

const SAMPLE_FILES = [
  {
    name: 'HR Employees Sample',
    filename: 'hr_employees_sample.csv',
    path: '/sample_data/hr_employees_sample.csv',
    description: '15 employee records with typical HR demographics',
    columns: ['employee_id', 'name', 'age_range', 'department', 'tenure_years', 'job_level', 'location', 'education'],
    useCase: 'HR policies, engagement surveys, benefit programs'
  },
  {
    name: 'Market Customers Sample',
    filename: 'market_customers_sample.csv',
    path: '/sample_data/market_customers_sample.csv',
    description: '15 customer records with purchasing behavior data',
    columns: ['customer_id', 'age_range', 'income_bracket', 'region', 'purchase_frequency', 'product_interest', 'loyalty_tier'],
    useCase: 'Product launches, pricing studies, segmentation'
  },
  {
    name: 'Stakeholders Sample',
    filename: 'stakeholders_sample.csv',
    path: '/sample_data/stakeholders_sample.csv',
    description: '10 stakeholder records for organizational analysis',
    columns: ['stakeholder_id', 'role', 'seniority', 'industry_years', 'region', 'company_size', 'previous_stance'],
    useCase: 'Change management, policy adoption, strategic initiatives'
  }
];

const TIPS = [
  {
    title: 'Better Data = Better Results',
    description: 'The more detailed your population data, the more accurate and nuanced the simulations will be. Include diverse demographics.',
    icon: Lightbulb
  },
  {
    title: 'Use Context Documents',
    description: 'When creating stimuli, add context documents (policies, announcements) to help AI understand the full scenario.',
    icon: FileUp
  },
  {
    title: 'Compare Scenarios',
    description: 'Create multiple stimuli with slight variations to A/B test different phrasings or policy options.',
    icon: BarChart3
  },
  {
    title: 'Open-ended for Depth',
    description: 'Use open-ended stimuli when you want to discover themes and nuances you might not anticipate.',
    icon: MessageSquareText
  }
];

const FAQS = [
  {
    question: 'How accurate are the AI-generated responses?',
    answer: 'SynPop uses GPT-4o-mini to generate probabilistically realistic responses based on demographic patterns. While not actual survey data, the responses reflect plausible behavioral patterns informed by extensive training data. Best used for scenario planning and preliminary insights.'
  },
  {
    question: 'What file formats are supported for population upload?',
    answer: 'SynPop supports CSV and Excel (.xlsx) files. Your file should have one row per individual with columns representing demographic attributes. First row should contain column headers.'
  },
  {
    question: 'Can I reuse the same stimulus with different populations?',
    answer: 'Absolutely! Stimuli are reusable templates. Create a stimulus once and run it against multiple populations to compare results across different groups.'
  },
  {
    question: 'How long does a simulation take to complete?',
    answer: 'Simulation time depends on population size. Typically: <500 individuals: 1-2 minutes, 500-2000: 2-4 minutes, 2000+: 4-8 minutes. You\'ll receive a notification when complete.'
  },
  {
    question: 'Can I export the simulation results?',
    answer: 'Yes! Results can be exported to Excel, CSV, or PDF. You can include or exclude AI reasoning and confidence scores in your export.'
  },
  {
    question: 'What do the Response Metrics mean?',
    answer: 'Response Metrics describe the shape of your simulation data. Consensus Score measures agreement level (higher = more consensus, lower = diverse opinions). Diversity (Entropy) indicates response variation using Shannon entropy — higher values mean more varied responses. Model Certainty is the average confidence of AI agents when generating responses. These are descriptive metrics. For result reliability, check the Reliability tab.'
  },
  {
    question: 'What is a good Consensus Score vs Diversity Score?',
    answer: 'Neither is inherently "better" — it depends on context. High Consensus (70%+) suggests clear majority opinion, useful for straightforward decisions. High Diversity (entropy >1.5) indicates varied perspectives, valuable for understanding the full range of reactions. For whether results are reliable enough to act on, see the Reliability tab.'
  }
];

const TROUBLESHOOTING = [
  {
    issue: 'File upload fails or shows validation errors',
    solution: 'Ensure your file has consistent column types, no completely empty columns, and at least 10 rows. Check that the first row contains headers.'
  },
  {
    issue: 'Simulation takes longer than expected',
    solution: 'Large populations (2000+) naturally take longer. Check your internet connection. If simulation fails, try running again or contact support.'
  },
  {
    issue: 'Response distribution looks too uniform',
    solution: 'This can happen with insufficient context. Add more detailed demographics or include context documents to help AI generate more nuanced responses.'
  },
  {
    issue: 'Cannot find my completed simulation',
    solution: 'Check the Simulations page and ensure you\'re viewing the correct client (if admin). Use the filter or search functionality to locate specific simulations.'
  },
  {
    issue: 'Response metrics show low consensus',
    solution: 'Low consensus means opinions are spread across options — this is normal for polarizing topics. Check the Reliability tab for whether results are still actionable. Adding context documents or more specific questions can help sharpen responses.'
  }
];

export default function Guide() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(1);
  const [activeUseCase, setActiveUseCase] = useState(1);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const handleDownload = (file) => {
    const link = document.createElement('a');
    link.href = file.path;
    link.download = file.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-8 max-w-6xl mx-auto page-transition">
      {/* Header */}
      <div className="text-center space-y-3 animate-fade-in">
        <div className="flex items-center justify-center gap-2">
          <BookOpen className="w-8 h-8 text-blue-600 icon-pulse" />
          <h1 className="text-4xl font-bold text-slate-900 dark:text-white">Getting Started with SynPop</h1>
        </div>
        <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
          Learn how to simulate population responses in 4 simple steps
        </p>
      </div>

      {/* Progress Indicator */}
      <div className="flex items-center justify-center gap-3 card-entrance">
        {STEPS.map((step, idx) => (
          <div key={step.id} className="flex items-center">
            <button
              onClick={() => setActiveStep(step.id)}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg transition-all",
                activeStep === step.id
                  ? "bg-primary text-white shadow-md"
                  : "bg-white dark:bg-card border border-slate-200 dark:border-border text-slate-600 dark:text-slate-300 hover:border-blue-300 dark:hover:border-blue-500/40"
              )}
            >
              <span className="font-semibold">{step.id}</span>
              <step.icon className="w-4 h-4" />
            </button>
            {idx < STEPS.length - 1 && (
              <ChevronRight className="w-5 h-5 text-slate-300 mx-1" />
            )}
          </div>
        ))}
      </div>

      {/* Active Step Detail */}
      {STEPS.filter(s => s.id === activeStep).map(step => (
        <Card key={step.id} className="border-2 border-blue-200 dark:border-blue-500/30 bg-gradient-to-br from-blue-50/30 to-white dark:from-blue-500/5 dark:to-card card-entrance">
          <CardContent className="p-8">
            <div className="flex items-start gap-6">
              <div className={cn("p-4 rounded-xl flex-shrink-0", step.color)}>
                <step.icon className="w-10 h-10" />
              </div>
              <div className="flex-1 space-y-5">
                <div>
                  <Badge variant="outline" className="mb-2">Step {step.id} of 4</Badge>
                  <h2 className="text-3xl font-bold text-slate-900 dark:text-white">{step.title}</h2>
                  <p className="text-slate-600 dark:text-slate-300 mt-2 text-lg">{step.description}</p>
                </div>
                
                <div className="space-y-3">
                  <p className="font-semibold text-slate-700 dark:text-slate-200">What to do:</p>
                  {step.details.map((detail, idx) => (
                    <div key={idx} className="flex items-start gap-3 animate-slide-up" style={{ animationDelay: `${idx * 0.1}s` }}>
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{detail}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-lg p-4">
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-300 mb-1">Expected Outcome:</p>
                  <p className="text-sm text-amber-800 dark:text-amber-200">{step.expectedOutcome}</p>
                </div>

                <Button 
                  onClick={() => navigate(step.action.path)}
                  className="mt-4 bg-primary hover:bg-primary/90 btn-hover-lift"
                  size="lg"
                >
                  {step.action.label}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Workflow Diagram */}
      <Card className="border border-slate-200 dark:border-border card-entrance">
        <CardHeader>
          <CardTitle className="text-xl">Complete Workflow</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-2 overflow-x-auto py-4">
            {STEPS.map((step, idx) => (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center min-w-[120px]">
                  <div className={cn("w-14 h-14 rounded-full flex items-center justify-center mb-2 transition-transform hover:scale-110", step.color)}>
                    <step.icon className="w-7 h-7" />
                  </div>
                  <span className="text-xs text-slate-600 dark:text-slate-300 text-center font-medium">{step.title}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <ChevronRight className="w-8 h-8 text-slate-300 mx-2" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Use Cases Section */}
      <div className="space-y-5">
        <div className="flex items-center gap-3 animate-fade-in">
          <Sparkles className="w-6 h-6 text-amber-500" />
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Real-World Use Cases</h2>
        </div>
        
        {/* Use Case Selector */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {USE_CASES.map((useCase) => (
            <button
              key={useCase.id}
              onClick={() => setActiveUseCase(useCase.id)}
              className={cn(
                "p-4 rounded-xl border-2 text-left transition-all card-entrance",
                activeUseCase === useCase.id
                  ? "border-blue-500 dark:border-blue-500/50 bg-blue-50/50 dark:bg-blue-500/10 shadow-md"
                  : "border-slate-200 dark:border-border bg-white dark:bg-card hover:border-slate-300 dark:hover:border-slate-600"
              )}
              style={{ animationDelay: `${useCase.id * 0.1}s` }}
            >
              <useCase.icon className={cn("w-6 h-6 mb-2", activeUseCase === useCase.id ? "text-blue-600 dark:text-blue-400" : "text-slate-400")} />
              <h3 className="font-semibold text-sm text-slate-900 dark:text-white">{useCase.title.split(': ')[1]}</h3>
            </button>
          ))}
        </div>

        {/* Active Use Case Details */}
        {USE_CASES.filter(uc => uc.id === activeUseCase).map(useCase => (
          <Card key={useCase.id} className="border-2 border-slate-200 dark:border-border shadow-lg animate-scale-in">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-white dark:from-muted/50 dark:to-card border-b border-slate-100 dark:border-border">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-50 dark:bg-blue-500/15 rounded-lg">
                  <useCase.icon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-xl">{useCase.title}</CardTitle>
                  <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{useCase.scenario}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Population */}
                <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <p className="font-semibold text-blue-900 dark:text-blue-300 text-sm">Population</p>
                  </div>
                  <p className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">{useCase.population.name}</p>
                  <p className="text-xs text-blue-700 dark:text-blue-300">{useCase.population.size}</p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">{useCase.population.demographics}</p>
                </div>

                {/* Stimulus */}
                <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-100 dark:border-amber-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    <p className="font-semibold text-amber-900 dark:text-amber-300 text-sm">Stimulus</p>
                  </div>
                  <p className="text-xs text-amber-800 dark:text-amber-200 mb-2 italic">"{useCase.stimulus.question}"</p>
                  <div className="flex gap-2">
                    <Badge variant="outline" className="text-xs bg-white dark:bg-card">{useCase.stimulus.type}</Badge>
                    <Badge variant="outline" className="text-xs bg-white dark:bg-card">{useCase.stimulus.scale}</Badge>
                  </div>
                </div>

                {/* Results */}
                <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart3 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    <p className="font-semibold text-emerald-900 dark:text-emerald-300 text-sm">Key Finding</p>
                  </div>
                  <p className="text-sm font-bold text-emerald-800 dark:text-emerald-200">{useCase.results.headline}</p>
                </div>
              </div>

              {/* Insights */}
              <div>
                <p className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-amber-500" />
                  Detailed Insights:
                </p>
                <div className="space-y-2">
                  {useCase.results.insights.map((insight, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>{insight}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actionable */}
              <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-500/10 border-2 border-blue-200 dark:border-blue-500/30">
                <p className="font-semibold text-blue-900 dark:text-blue-300 mb-1 text-sm flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Actionable Recommendation:
                </p>
                <p className="text-sm text-blue-800 dark:text-blue-200">{useCase.actionable}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Sample Data Downloads */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 animate-fade-in">
          <Download className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Sample Data Files</h2>
        </div>
        <p className="text-slate-600 dark:text-slate-300 text-sm">Download these sample CSV files to get started quickly</p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {SAMPLE_FILES.map((file, idx) => (
            <Card key={idx} className="border border-slate-200 dark:border-border hover:shadow-md transition-all card-entrance" style={{ animationDelay: `${idx * 0.1}s` }}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 bg-blue-50 dark:bg-blue-500/15 rounded-lg">
                    <FileUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <Button
                    onClick={() => handleDownload(file)}
                    variant="outline"
                    size="sm"
                    className="btn-hover-lift"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{file.name}</h3>
                <p className="text-xs text-slate-600 dark:text-slate-400 mb-3">{file.description}</p>
                <div className="space-y-2">
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Columns included:</p>
                  <div className="flex flex-wrap gap-1">
                    {file.columns.slice(0, 4).map((col, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{col}</Badge>
                    ))}
                    {file.columns.length > 4 && (
                      <Badge variant="outline" className="text-xs">+{file.columns.length - 4} more</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2"><span className="font-medium">Use for:</span> {file.useCase}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Tips Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 animate-fade-in">
          <Lightbulb className="w-6 h-6 text-amber-500" />
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Pro Tips</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {TIPS.map((tip, idx) => (
            <Card key={idx} className="border border-slate-200 dark:border-border hover:shadow-md transition-all card-entrance" style={{ animationDelay: `${idx * 0.1}s` }}>
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-50 dark:bg-amber-500/15 rounded-lg flex-shrink-0">
                    <tip.icon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{tip.title}</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-300">{tip.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 animate-fade-in">
          <HelpCircle className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Frequently Asked Questions</h2>
        </div>
        <div className="space-y-3">
          {FAQS.map((faq, idx) => (
            <Card 
              key={idx} 
              className="border border-slate-200 dark:border-border overflow-hidden cursor-pointer hover:border-slate-300 dark:hover:border-slate-600 transition-all card-entrance"
              style={{ animationDelay: `${idx * 0.05}s` }}
              onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900 dark:text-white text-sm">{faq.question}</h3>
                  <ChevronRight className={cn("w-5 h-5 text-slate-400 transition-transform", expandedFaq === idx && "rotate-90")} />
                </div>
                <div className={cn(
                  "height-transition overflow-hidden",
                  expandedFaq === idx ? "max-h-96 opacity-100 mt-3" : "max-h-0 opacity-0"
                )}>
                  <p className="text-sm text-slate-600 dark:text-slate-300">{faq.answer}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Troubleshooting */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 animate-fade-in">
          <AlertCircle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Troubleshooting</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {TROUBLESHOOTING.map((item, idx) => (
            <Card key={idx} className="border border-orange-100 dark:border-orange-500/20 bg-orange-50/30 dark:bg-orange-500/5 card-entrance" style={{ animationDelay: `${idx * 0.1}s` }}>
              <CardContent className="p-5">
                <p className="font-semibold text-orange-900 dark:text-orange-300 text-sm mb-2">{item.issue}</p>
                <p className="text-sm text-orange-800 dark:text-orange-200">{item.solution}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="text-center py-8 card-entrance">
        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Ready to Get Started?</h3>
        <p className="text-slate-600 dark:text-slate-300 mb-6">Create your first simulation in minutes</p>
        <Button 
          onClick={() => navigate('/populations')}
          size="lg"
          className="bg-primary hover:bg-primary/90 btn-hover-lift"
        >
          <Upload className="w-5 h-5 mr-2" />
          Upload Your First Population
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );
}
